import { Component, Input } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ArrayCheckboxOptions } from '../model/component-option/arrayCheckbox.model';
import { GenericFormService } from '../service/generic-form.service';
import { FieldModel } from '../model/field.model';

@Component({
	selector: 'cv-array-checkbox-field',
	templateUrl: './array-checkbox-field.component.html',
	styleUrls: ['./array-checkbox-field.component.scss']
})
export class ArrayCheckboxFieldComponent {
	@Input() field: FieldModel;
	@Input() fieldOptions: any;
	parentFormGroup: FormArray;

	get arrayCheckboxComponentOptions(): ArrayCheckboxOptions {
		return this.field.UIOptions.componentOptions.arrayCheckbox;
	}

	get groupName(): string {
		return this.arrayCheckboxComponentOptions.groupName;
	}

	get checkboxValue(): string {
		return this.arrayCheckboxComponentOptions.value;
	}

	get label(): string {
		return this.arrayCheckboxComponentOptions.label;
	}

	get groupKey(): string {
		return this.arrayCheckboxComponentOptions?.groupKey;
	}

	get valueKey(): string {
		return this.arrayCheckboxComponentOptions?.valueKey;
	}

	get inputId(): string {
		return `${this.groupName}-${this.checkboxValue}`;
	}

	constructor(private formService: GenericFormService) { }

	ngOnInit() {
		this.parentFormGroup = this.formService.form.get(this.fieldOptions.path) as FormArray;
	}

	/**
	 * Handles the checkbox toggle. Adds the checkbox value to the form array when
	 * checked, removes it when unchecked, and marks the form array as dirty.
	 * @param checked - new checked state of the checkbox
	 */
	onToggle(checked: boolean): void {
		checked ? this.addValue() : this.removeValue();
		this.parentFormGroup.markAsDirty();
	};

	/**
	 * Finds the form group in the form array whose group key matches this checkbox group.
	 * @returns the matching form group, or null if it does not exist
	 */
	isFormGroupExists(): FormGroup {
		if (!this.groupKey) {
			return null;
		}

		const value = this.parentFormGroup.value;
		const index = value.findIndex((item) => item[this.groupKey] == this.groupName);
		if (index > -1) {
			return this.parentFormGroup.at(index) as FormGroup;
		}

		return null;
	}

	/**
	 * Checks whether this checkbox value is present in its group's value list.
	 * @returns true if the checkbox should be shown as checked
	 */
	checked(): boolean {
		const formGroup = this.isFormGroupExists();
		if (formGroup) {
			return !!formGroup.get(this.valueKey)?.value?.includes(this.checkboxValue);
		}

		return false;
	}

	/**
	 * Adds the checkbox value to its group. Creates a new group entry in the form
	 * array if the group does not exist yet, otherwise pushes the value to it.
	 */
	private addValue(): void {
		const fieldFormGroup = this.isFormGroupExists();
		if (fieldFormGroup == null) {
			this.formService.createChilds(this.fieldOptions.path, this.getNewArrayItem());
		} else {
			const valueFormArray = fieldFormGroup.get(this.valueKey) as FormArray;
			valueFormArray.push(new FormControl(this.checkboxValue));
		}
	}


	/**
	 * Builds a new form array item for this group and checkbox value using the
	 * buildArrayItem function from the component options.
	 * @returns the new array item
	 */
	private getNewArrayItem(): any {
		const buildArrayItem = this.arrayCheckboxComponentOptions.buildArrayItem;

		if (typeof buildArrayItem === 'function') {
			return buildArrayItem(this.groupName, this.checkboxValue);
		}

		return null;
	}

	/**
	 * Removes the checkbox value from its group, and removes the whole group entry
	 * from the form array once no values are left.
	 */
	private removeValue(): void {
		const fieldFormGroup = this.isFormGroupExists();
		if (!fieldFormGroup) {
			return;
		}

		const valueFormArray = fieldFormGroup.get(this.valueKey) as FormArray;
		if (!valueFormArray) {
			return;
		}

		const index = valueFormArray.value.indexOf(this.checkboxValue);
		if (index > -1) {
			valueFormArray.removeAt(index);
		}

		// Remove the whole group entry once its last value is switched off
		if (!valueFormArray.length) {
			const groupIndex = this.parentFormGroup.controls.indexOf(fieldFormGroup);
			this.parentFormGroup.removeAt(groupIndex);
		}
	}
}
